/**
 * Domain models for the global AI Chat Assistant (Leads-only v1).
 *
 * Contract source of truth: docs/ai-assistant-api.md.
 *
 * The backend owns intent understanding, tool routing AND execution. The frontend is
 * thin: it sends the user's text, renders the assistant `reply`, and — when the reply
 * carries a `pending_action` (a write) — shows Confirm / Cancel and posts the decision
 * back. The server performs the actual CRM mutation; the frontend never calls
 * LeadsService to execute an assistant action.
 *
 * Office scope is automatic (derived from the JWT) — no `office_id` is sent.
 */

export type ChatRole = 'user' | 'assistant';

/** Standard Remmi response envelope. */
export interface AiApiEnvelope<T> {
  status: boolean;
  message?: string;
  data: T;
}

/** A friendly label/value row for the confirmation card. */
export interface AiConfirmationField {
  label: string;
  value: string;
}

/**
 * An editable dropdown the user fills inside the confirmation card (e.g. on `create_lead`:
 * lead type / enquiry type / source / status). Options come from the office config; the picked
 * value is sent back in the confirm `values` object.
 */
export interface AiConfirmationDropdown {
  /** Machine key the chosen value is sent under, e.g. `lead_status`. */
  field: string;
  /** User-facing label, e.g. "Lead Status". */
  label: string;
  /** When true the user must pick a value before confirming. */
  required?: boolean;
  /** Prefilled selection (may be empty). */
  value?: string;
  /** Valid options for this field (office-specific). */
  options: string[];
}

/**
 * Backend-built, user-facing confirmation payload. This is what the confirm card renders —
 * NOT {@link AiPendingAction.args}. It carries only clean labels/values (no ids, no raw keys).
 * Every write tool returns this exact shape.
 */
export interface AiConfirmation {
  /** Heading for the card — the lead NAME, never an id. */
  lead_name?: string;
  /** Label/value rows rendered verbatim. */
  fields?: AiConfirmationField[];
  /** Editable dropdowns the user fills before confirming (e.g. create_lead type/source/status). */
  dropdowns?: AiConfirmationDropdown[];
  /** Bulk confirmation: how many records this one card will close/complete/move. */
  count?: number;
  /** Bulk confirmation: the affected records (names) so the user sees everything that will change. */
  items?: AiConfirmationItem[];
  /** Machine field: pinned target lead. Use for an "Open Lead" link only — never displayed. */
  lead_id?: string;
}

/** An affected record in a bulk confirmation (name shown; id never displayed). */
export interface AiConfirmationItem {
  id?: string;
  name?: string;
}

/**
 * User-supplied field values sent with a confirm (`approve: true`) and merged into the action's
 * args server-side before it runs. Keyed by field (e.g. `lead_status`, `reason`); the backend
 * validates each against the office options and re-prompts when one is invalid.
 */
export type AiConfirmValues = Record<string, string>;

/** A write action awaiting the user's explicit confirmation. */
export interface AiPendingAction {
  action_id: string;
  /** e.g. `create_lead`. */
  tool: string;
  /** Human summary to render next to Confirm, e.g. `Create lead "Sarah Khan" (Buyer)?`. */
  summary: string;
  /**
   * User-facing confirmation card content (heading + friendly label/value rows). Render THIS.
   * Falls back to {@link summary} when absent.
   */
  confirmation?: AiConfirmation | null;
  /** Sanitized machine payload (no ids). Backend-only — must NOT be rendered to the user. */
  args: Record<string, any>;
}

/** Read tools the assistant ran this turn (optional to display). */
export interface AiActionTaken {
  tool: string;
  kind: string;
}

/**
 * A lead the assistant surfaced for a read/search turn. Rendered as a clickable table
 * row in the chat; clicking opens that exact lead via `/leads?id=<id>`.
 * `id` is required for navigation; the remaining fields are best-effort display columns.
 */
export interface AiLeadResult {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  status?: string;
  type?: string;
  /** Assigned agent display name (agent_responsible). */
  agent?: string;
  /** Project / development the lead belongs to (shown in the card subtitle). */
  project?: string;
  /** Lead source, e.g. "UTM · Google Ads". */
  source?: string;
  /** Temperature, e.g. "Hot" | "Warm" | "Cold". */
  temperature?: string;
  /** The lead's project (machine id), or null when unlinked (Round 5). Opens the project; never shown. */
  project_id?: string | null;
}

// ---- Structured `views[]` channel (FRONTEND_HANDOFF §0) ----------------------
//
// `POST /chat` and `POST /confirm` now return a `views[]` array alongside `reply`.
// Render `views` as structured cards; treat `reply` as a short caption only. Each
// view is a discriminated union on `type`. Unknown future types are ignored by the
// renderer (forward-compatible), so adding a new module is a render-only change.

/** A single lead card inside a `lead_list` / `lead_detail` / `lead_created` view. */
export interface AiViewLeadItem {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  lead_status?: string;
  lead_type?: string;
  lead_source?: string;
  assigned_agent?: string;
  assigned_agent_id?: string;
  created_at?: string;
  /** Present on `lead_detail`. */
  temperature?: string;
  /** Present on `lead_detail` and `find_follow_ups` items. */
  follow_up_date?: string;
  /** Present on `lead_created` — optional fields the user may want to add later. */
  missing_optional?: string[];
  /** Present on `lead_created` — whether a requested project was linked to the new lead. */
  project_attach?: 'linked' | 'not_found' | null;
  /** Present on `lead_created` — the project name the user asked to attach. */
  project_name?: string;
  /**
   * The lead's project (machine id from `leads.project`), or null when unlinked (Round 5).
   * MACHINE field — never displayed. Used to open the lead's project when the lead is opened.
   */
  project_id?: string | null;
}

/**
 * Request to open a lead from an assistant card. When `projectId` is set (Round 5), opening
 * navigates to the lead's PROJECT (canonical project route); when null it opens the lead itself.
 */
export interface AiOpenLeadRequest {
  leadId: string;
  projectId?: string | null;
}

/** A contact card inside a `contact_list` view. */
export interface AiViewContactItem {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
}

/** Shared pagination shape on list views (`lead_list`, `contact_list`). */
export interface AiViewPaging {
  total: number;
  offset: number;
  limit: number;
  has_more: boolean;
  next_offset?: number;
}

/** A button the backend attaches to a view (e.g. "Open Lead" on `lead_created`). */
export interface AiViewAction {
  label: string;
  /** e.g. `open_lead`. */
  action: string;
  lead_id?: string;
}

export interface AiLeadListView extends AiViewPaging {
  type: 'lead_list';
  items: AiViewLeadItem[];
  /** True when a name matched several leads — the list is a disambiguation picker. */
  ambiguous?: boolean;
}

export interface AiLeadDetailView {
  type: 'lead_detail';
  item: AiViewLeadItem;
}

export interface AiLeadCreatedView {
  type: 'lead_created';
  item: AiViewLeadItem;
  actions?: AiViewAction[];
}

export interface AiLeadUpdatedView {
  type: 'lead_updated';
  item: { id: string; name?: string; updated_fields?: string[] };
  actions?: AiViewAction[];
}

export interface AiFollowUpSavedView {
  type: 'follow_up_saved';
  item: { lead_id: string; name?: string; due_date?: string; task_time?: string };
  actions?: AiViewAction[];
}

export interface AiFollowUpCompletedView {
  type: 'follow_up_completed';
  item: { lead_id: string; name?: string };
  actions?: AiViewAction[];
}

export interface AiContactLoggedView {
  type: 'contact_logged';
  item: { lead_id: string; name?: string; channels?: string[] };
  actions?: AiViewAction[];
}

export interface AiAppointmentItem {
  id: string;
  title?: string;
  type?: string;
  date?: string;
  start_time?: string;
  end_time?: string;
  is_inspection?: boolean;
  summary?: string;
  /** The lead this appointment belongs to (present on `my_appointment_list`); opens the lead. */
  lead_id?: string;
}

export interface AiAppointmentListView {
  type: 'appointment_list';
  lead_id?: string;
  items: AiAppointmentItem[];
}

/**
 * "Show my appointments/inspections" — the user's own upcoming appointments (from
 * `list_my_appointments`). Splits appointments vs inspections; each item may carry a `lead_id`
 * so the row opens the linked lead.
 */
export interface AiMyAppointmentListView {
  type: 'my_appointment_list';
  /** e.g. "today" | "this week" — the period the list covers. */
  period?: string;
  /** e.g. "appointments" | "inspections" | "all" — what was asked for. */
  kind?: string;
  appointments_count?: number;
  inspections_count?: number;
  items: AiAppointmentItem[];
}

export interface AiAppointmentBookedView {
  type: 'appointment_booked';
  item: { lead_id: string; name?: string; title?: string; date?: string; time?: string };
  actions?: AiViewAction[];
}

export interface AiStatBucket {
  value: string;
  count: number;
}

export interface AiLeadStatsView {
  type: 'lead_stats';
  total: number;
  by_status?: AiStatBucket[];
  by_type?: AiStatBucket[];
}

export interface AiContactListView extends AiViewPaging {
  type: 'contact_list';
  items: AiViewContactItem[];
}

/** Dynamic leads-grid column metadata (same shape as `POST /get-all-leads-new`). */
export interface AiLeadGridColumn {
  type: string;
  field: string;
  column: string;
  /** Optional friendlier heading (added round 2); falls back to `column`. */
  label?: string;
}

export interface AiLeadGridView {
  type: 'lead_grid';
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  view?: { id: string; name: string; selected?: boolean } | null;
  columns: AiLeadGridColumn[];
  rows: Array<Record<string, any>>;
}

export interface AiLeadViewItem {
  id: string;
  name: string;
  selected?: boolean;
  is_default?: boolean;
  columns?: string[];
}

export interface AiLeadViewsView {
  type: 'lead_views';
  items: AiLeadViewItem[];
}

export interface AiLeadViewSavedView {
  type: 'lead_view_saved';
  item: { id: string; name: string; columns?: string[] };
}

export interface AiCapabilityCategory {
  category: string;
  available: boolean;
  examples?: string[];
}

export interface AiSuggestedPrompt {
  label: string;
  prompt: string;
}

export interface AiCapabilitiesView {
  type: 'capabilities';
  available_categories: AiCapabilityCategory[];
  coming_soon?: string[];
  suggested_prompts?: AiSuggestedPrompt[];
}

/** A lead row inside a day-briefing / follow-ups-moved view (permissive — fields are best-effort). */
export interface AiBriefingItem {
  lead_id?: string;
  id?: string;
  name?: string;
  /** The lead's project (machine id), or null when unlinked (Round 5). Opens the project; never shown. */
  project_id?: string | null;
  /** Lead status, e.g. "New". Backend may send either `status` or `lead_status`. */
  status?: string;
  lead_status?: string;
  /** Lead type, e.g. "Buyer". Backend may send either `type` or `lead_type`. */
  type?: string;
  lead_type?: string;
  /** Appointment/inspection title (briefing rows for appointments use this over `name`). */
  title?: string;
  /** Appointment start time, e.g. "09:00 AM" (day-briefing appointment rows). */
  start_time?: string;
  /** True when an appointment row is an inspection. */
  is_inspection?: boolean;
  follow_up_date?: string;
  follow_up_time?: string;
  task_time?: string;
  time?: string;
}

/** "Tell me about my day" — today's follow-ups, overdue count, new leads, and appointments. */
export interface AiDayBriefingView {
  type: 'day_briefing';
  greeting_name?: string;
  follow_ups_today?: { count: number; items?: AiBriefingItem[] };
  follow_ups_overdue?: { count: number; items?: AiBriefingItem[] };
  new_leads_today?: { count: number; items?: AiBriefingItem[] };
  /** Today's appointments (with a sub-count of how many are inspections). */
  appointments_today?: { count: number; inspections?: number; items?: AiBriefingItem[] };
}

/** Confirmation after a bulk "move my morning follow-ups to this afternoon". */
export interface AiFollowUpsMovedView {
  type: 'follow_ups_moved';
  count: number;
  to_date?: string;
  to_time?: string;
  items?: AiBriefingItem[];
}

/** Result of a bulk close (`close_leads`): how many closed, the reason, and the affected leads. */
export interface AiLeadsClosedView {
  type: 'leads_closed';
  count: number;
  /** Records that couldn't be closed (shown as a soft warning when non-zero). */
  failed_count?: number;
  reason?: string;
  items?: AiBriefingItem[];
}

/**
 * Result of a bulk field update (`update_leads`): how many changed, which fields changed (the
 * applied `changes` map, e.g. `{ lead_status: "Contact Attempted" }`), and the affected leads.
 */
export interface AiLeadsUpdatedView {
  type: 'leads_updated';
  count: number;
  failed_count?: number;
  /** Applied change map — field key → new value (e.g. `{ lead_status: "Contact Attempted" }`). */
  changes?: Record<string, string>;
  items?: AiBriefingItem[];
}

/** Result of a bulk complete (`complete_follow_ups`): how many completed and the affected leads. */
export interface AiFollowUpsCompletedView {
  type: 'follow_ups_completed';
  count: number;
  failed_count?: number;
  items?: AiBriefingItem[];
}

/** Result of a bulk add (`add_follow_ups`): how many scheduled, the due date/time, affected leads. */
export interface AiFollowUpsAddedView {
  type: 'follow_ups_added';
  count: number;
  failed_count?: number;
  due_date?: string;
  task_time?: string;
  items?: AiBriefingItem[];
}

/**
 * Project module views (projects / lots / EOIs / project tasks / events). The backend's exact
 * per-item fields aren't fixed here — the renderer reads them defensively (primary label + a few
 * best-effort detail chips, ids hidden), so a new field needs no model change.
 */
export interface AiProjectView {
  type: 'project_list' | 'project_detail' | 'lot_list' | 'eoi_list' | 'project_task_list' | 'project_event_list';
  /** Machine field — use for context only, never displayed. */
  project_id?: string;
  project_name?: string;
  /** List rows (projects / lots / EOIs / tasks / events). */
  items?: Record<string, any>[];
  /** Single record for `project_detail` (some backends send `items[0]` instead). */
  item?: Record<string, any>;
  /** Paging (lot_list / eoi_list). */
  total?: number;
  page?: number;
  per_page?: number;
}

/** Discriminated union of every structured view the assistant can return. */
export type AiView =
  | AiLeadListView
  | AiLeadDetailView
  | AiLeadCreatedView
  | AiLeadUpdatedView
  | AiFollowUpSavedView
  | AiFollowUpCompletedView
  | AiContactLoggedView
  | AiAppointmentListView
  | AiMyAppointmentListView
  | AiAppointmentBookedView
  | AiLeadStatsView
  | AiContactListView
  | AiLeadGridView
  | AiLeadViewsView
  | AiLeadViewSavedView
  | AiDayBriefingView
  | AiFollowUpsMovedView
  | AiLeadsClosedView
  | AiLeadsUpdatedView
  | AiFollowUpsCompletedView
  | AiFollowUpsAddedView
  | AiProjectView
  | AiCapabilitiesView;

/** Result of a confirmed (or cancelled) write, returned by `POST /ai-assistant/confirm`. */
export interface AiActionResult {
  ok?: boolean;
  lead_id?: string;
  message?: string;
  error?: string;
  /** Business-rule fields still required; the user supplies them via a new /chat message. */
  required_fields?: string[];
}

/** Quick live counts shown as a badge next to the greeting offer (best-effort; may be null). */
export interface AiGreetingDay {
  date?: string;
  follow_ups_today?: number;
  follow_ups_overdue?: number;
  new_leads_today?: number;
  appointments_today?: number;
  inspections_today?: number;
}

/** `data` payload from `GET /ai-assistant/greeting` — a timezone-aware welcome (§0a). */
export interface AiGreeting {
  /** Ready-to-display line, e.g. "Good morning, Rosina." */
  greeting: string;
  greeting_name?: string | null;
  part_of_day?: 'morning' | 'afternoon' | 'evening';
  timezone?: string;
  local_time?: string;
  /** The offer to show under the greeting, e.g. "Would you like me to run through your day?" */
  prompt?: string;
  /** Best-effort snapshot for a quick badge; null when it couldn't be computed. */
  day?: AiGreetingDay | null;
}

/** `data` payload from `POST /ai-assistant/chat`. */
export interface AiChatResponse {
  conversation_id: string;
  reply: string;
  requires_confirmation: boolean;
  pending_action?: AiPendingAction | null;
  /** Total writes queued from one message; > 1 means more confirmations follow this one. */
  pending_count?: number;
  actions_taken?: AiActionTaken[];
  /**
   * Structured leads for a read/search turn; rendered as a clickable table.
   * @deprecated Superseded by {@link views}; kept for backward-compat parsing.
   */
  leads?: AiLeadResult[] | null;
  /** Structured cards to render (FRONTEND_HANDOFF §0) — the primary result channel. */
  views?: AiView[] | null;
  provider?: string;
  model?: string;
}

/** `data` payload from `POST /ai-assistant/confirm`. */
export interface AiConfirmResponse {
  conversation_id: string;
  reply: string;
  requires_confirmation: boolean;
  pending_action?: AiPendingAction | null;
  /** Writes still queued (this one's result is in `action_result`); drives the "N left" hint. */
  pending_count?: number;
  action_result?: AiActionResult;
  cancelled?: boolean;
  /** Structured cards to render (FRONTEND_HANDOFF §0) — e.g. `lead_created`, `lead_view_saved`. */
  views?: AiView[] | null;
}

/** Item from `GET /ai-assistant/conversations`. */
export interface AiConversationSummary {
  id: string;
  title: string;
  modules: string[];
  updated_at: string;
  created_at: string;
}

/**
 * A persisted transcript turn from `GET /ai-assistant/conversations/:id`.
 *
 * Round 4 (FRONTEND_HANDOFF): each assistant message carries the SAME structured payload it
 * had live, so a reopened conversation re-renders its cards. `views` mirror live `data.views`;
 * `confirmation` mirrors live `pending_action.confirmation` (a historical write proposal);
 * `action_result` mirrors live `data.action_result` (the created/updated/closed result).
 */
export interface AiTranscriptMessage {
  role: ChatRole;
  content: string;
  created_at: string;
  /** Per-message structured cards (tables/lists/detail/briefing) — same renderer as live. */
  views?: AiView[] | null;
  /** Per-message confirmation card content (a write turn's proposal); read this, NOT pending_action. */
  confirmation?: AiConfirmation | null;
  /** Per-message write result (created/updated/closed) — success renders `views`, failure the error. */
  action_result?: AiActionResult | null;
}

/** `data` payload from `GET /ai-assistant/conversations/:id`. */
export interface AiConversationDetail {
  id: string;
  title: string;
  modules: string[];
  pending_action?: AiPendingAction | null;
  messages: AiTranscriptMessage[];
}

/**
 * Lifecycle of the confirm card attached to an assistant message. `historical` is a reloaded
 * (Round 4) confirmation whose outcome lives on a later message — render it read-only, no badge.
 */
export type AiActionCardState = 'pending' | 'confirming' | 'cancelling' | 'confirmed' | 'cancelled' | 'failed' | 'historical';

/**
 * A chat bubble in the UI. A user bubble is plain text; an assistant bubble may carry a
 * `pendingAction` (renders the Confirm/Cancel card) or an `actionResult` (post-confirm).
 */
export interface AiChatMessage {
  /** Client-side id for trackBy / card targeting. */
  id: string;
  role: ChatRole;
  content: string;
  /** Epoch ms. */
  createdAt: number;
  /** True for the transient assistant "typing…" placeholder. */
  pending?: boolean;
  /** True when this message failed to send (offers a retry affordance). */
  errored?: boolean;
  /** The user prompt to resend when retrying a failed turn (transient 502/503 errors). */
  retryText?: string | null;
  /** The images (base64 data URLs) to resend with a retried turn. */
  retryImages?: string[] | null;
  /** Image attachments on a user bubble (base64 data URLs); rendered as thumbnails. */
  images?: string[] | null;
  /** Set on the assistant bubble that introduced a write action. */
  pendingAction?: AiPendingAction | null;
  actionCardState?: AiActionCardState;
  /** When several writes were queued from one message: how many remain after this one. */
  queuedRemaining?: number;
  /** Result of the confirmed write (drives a "View in Leads" affordance). */
  actionResult?: AiActionResult | null;
  /**
   * Structured leads from a read/search turn; rendered as a clickable table.
   * @deprecated Superseded by {@link views}; kept for the text-parsing fallback.
   */
  leads?: AiLeadResult[] | null;
  /** Structured cards (FRONTEND_HANDOFF §0) — the primary result channel. */
  views?: AiView[] | null;
}

/** Why the assistant may be unusable, so the empty-state shows the right copy. */
export type AiAssistantGate = 'ok' | 'no-permission' | 'flag-off';

export interface AiSuggestion {
  label: string;
  prompt: string;
  /**
   * Icon hint. The backend sends a logical name (e.g. `user-plus`); the UI maps it to a
   * PrimeIcons class via {@link aiSuggestionIcon}. A raw `pi-*` class is also accepted.
   */
  icon?: string;
  /** From `GET /suggestions`; a disabled chip is hidden. Defaults to true when absent. */
  enabled?: boolean;
}

/** `data` payload from `GET /ai-assistant/config` (suggestions + capabilities). */
export interface AiAssistantConfig {
  suggested_prompts?: AiSuggestion[];
  capabilities?: AiCapabilityCategory[];
}

// ---- AI usage / cost report (FRONTEND_HANDOFF §6) -----------------------------

export type AiUsageGranularity = 'day' | 'month';
export type AiUsageScope = 'office' | 'all';

export interface AiUsageTotals {
  interactions: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost_usd: number;
}

export interface AiUsageFeatureRow {
  feature: string;
  interactions?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
  cost_usd?: number;
}

export interface AiUsageModelRow {
  provider: string;
  model: string;
  interactions?: number;
  total_tokens?: number;
  cost_usd?: number;
}

export interface AiUsageSeriesPoint {
  period: string;
  interactions?: number;
  total_tokens?: number;
  cost_usd?: number;
}

/** `data` payload from `GET /ai-usage/report` (master_admin only). */
export interface AiUsageReport {
  granularity: AiUsageGranularity;
  office_id?: string | null;
  feature?: string | null;
  from?: string;
  to?: string;
  totals: AiUsageTotals;
  by_feature: AiUsageFeatureRow[];
  by_model: AiUsageModelRow[];
  series: AiUsageSeriesPoint[];
}

/** Query params for `GET /ai-usage/report`. */
export interface AiUsageReportQuery {
  granularity?: AiUsageGranularity;
  from?: string;
  to?: string;
  scope?: AiUsageScope;
  feature?: string;
}

/** Friendly labels for the known feature ids (FRONTEND_HANDOFF §6). */
export const AI_USAGE_FEATURE_LABELS: Record<string, string> = {
  assistant: 'Assistant',
  lead_summary: 'Lead summaries',
  project_summary: 'Project summaries',
  sales_presentation_hotspots: 'Sales presentation hotspots',
  lead_email_enrichment: 'Lead email enrichment',
  unknown: 'Untagged',
};

export function aiUsageFeatureLabel(feature: string): string {
  return AI_USAGE_FEATURE_LABELS[feature] || (feature || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** Logical icon name → PrimeIcons class. The single source for both the picker and renderer. */
export const AI_SUGGESTION_ICON_MAP: Record<string, string> = {
  'user-plus': 'pi-user-plus',
  user: 'pi-user',
  users: 'pi-users',
  plus: 'pi-plus',
  search: 'pi-search',
  eye: 'pi-eye',
  inbox: 'pi-inbox',
  chart: 'pi-chart-bar',
  'chart-bar': 'pi-chart-bar',
  'chart-line': 'pi-chart-line',
  calendar: 'pi-calendar',
  clock: 'pi-clock',
  bell: 'pi-bell',
  star: 'pi-star',
  list: 'pi-list',
  filter: 'pi-filter',
  tag: 'pi-tag',
  flag: 'pi-flag',
  phone: 'pi-phone',
  envelope: 'pi-envelope',
  home: 'pi-home',
  building: 'pi-building',
  check: 'pi-check-circle',
  bolt: 'pi-bolt',
  question: 'pi-question-circle',
  comment: 'pi-comment',
  sparkles: 'pi-sparkles',
};

/** Selectable icon options for the admin picker (logical name + resolved PrimeIcons class). */
export const AI_SUGGESTION_ICON_OPTIONS: { name: string; class: string }[] = Object.keys(AI_SUGGESTION_ICON_MAP).map(name => ({
  name,
  class: AI_SUGGESTION_ICON_MAP[name],
}));

/**
 * Map a backend icon hint to a PrimeIcons class. Accepts a raw `pi-*` class unchanged,
 * a known logical name, or falls back to a neutral chat icon.
 */
export function aiSuggestionIcon(icon?: string): string {
  if (!icon) {
    return 'pi-comment';
  }
  if (icon.startsWith('pi-')) {
    return icon;
  }
  return AI_SUGGESTION_ICON_MAP[icon] || 'pi-comment';
}

export const AI_WELCOME_SUGGESTIONS: AiSuggestion[] = [
  { label: 'Create New Lead', prompt: 'Create a new lead', icon: 'pi-plus' },
  { label: 'Search leads', prompt: 'Help me find a lead', icon: 'pi-search' },
  { label: 'Show unassigned leads', prompt: 'Show me unassigned leads', icon: 'pi-eye' },
];

export const AI_ASSISTANT_FEATURE_FLAG = 'ai_assistant_leads';

/** Permissions that grant access to the assistant FAB (any one is sufficient). */
export const AI_ASSISTANT_VIEW_PERMISSIONS = ['lead.view', 'lead.manage'];

/** sessionStorage key for same-tab conversation continuity. */
export const AI_CONVERSATION_STORAGE_KEY = 'ai-assistant:conversation-id';

/**
 * Message substrings the backend returns (HTTP 422) when an office can't use the assistant.
 * Used to show actionable "AI not set up" copy instead of a generic error.
 */
export const AI_NOT_CONFIGURED_HINTS = ['api key not configured', 'supports the openai provider', 'no active office'];
